/**
 * version-check — 偵測到新部署時提示更新,根治「開著舊分頁跑舊版」。
 *
 * 緣由(boss 2026-06-10):部署新版後,老闆開著的分頁仍跑舊 JS(沒重整)→ 看到舊行為
 * (沒進度條 / OCR 瞎掰),誤以為功能壞了。Vite 進入點是 `assets/main-<hash>.js`,
 * 每次 build 換 hash。本模組用目前載入的 main bundle 檔名當版本指紋,定期 + 回到分頁
 * 時抓首頁比對;不同 → 顯示「有新版本,點一下更新」橫幅(點了才重整,絕不在作答 /
 * 上傳到一半自動重整毀掉進度)。
 *
 * 視覺 = 學術風(--ef-* token)。零相依,純前端。
 */

/** 從 HTML 文字抓進入點 bundle 檔名(版本指紋);沒有回 null。 */
export function extractBundleId(html: string): string | null {
  const m = html.match(/assets\/main-[\w-]+\.js/);
  return m ? m[0] : null;
}

/** 目前頁面實際載入的進入點 bundle 檔名。 */
function loadedBundleId(): string | null {
  if (typeof document === 'undefined') return null;
  const scripts = Array.from(document.querySelectorAll('script[src]'));
  for (const s of scripts) {
    const src = s.getAttribute('src') ?? '';
    const m = src.match(/assets\/main-[\w-]+\.js/);
    if (m) return m[0];
  }
  return null;
}

/** 抓首頁(不走快取)取最新 bundle 指紋。失敗回 null(離線等)。 */
async function fetchLatestBundleId(): Promise<string | null> {
  try {
    const res = await fetch(`/?_v=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return null;
    return extractBundleId(await res.text());
  } catch {
    return null;
  }
}

/** 顯示「更新」橫幅(冪等;點一下重整)。 */
export function showUpdateBanner(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById('ef-update-banner')) return;
  const btn = document.createElement('button');
  btn.id = 'ef-update-banner';
  btn.type = 'button';
  btn.textContent = '🔄 有新版本,點一下更新';
  btn.style.cssText =
    'position:fixed;left:50%;bottom:18px;transform:translateX(-50%);z-index:9999;' +
    'padding:10px 20px;border-radius:999px;border:1px solid var(--ef-green,#1f6f5c);' +
    'background:var(--ef-green,#1f6f5c);color:#fff;font-family:inherit;font-size:14px;font-weight:700;' +
    'box-shadow:0 4px 16px rgba(0,0,0,0.18);cursor:pointer;letter-spacing:0.5px;';
  btn.addEventListener('click', () => {
    // location 可能在某些環境唯讀;盡力重整。
    try { window.location.reload(); } catch { /* noop */ }
  });
  document.body.appendChild(btn);
}

/**
 * 啟動版本偵測。在 app 啟動時呼叫一次。
 * - 回到分頁(visibilitychange → visible)時檢查(最常見的重整時機)。
 * - 每 5 分鐘檢查一次當作保底。
 * - 啟動 30s 後先檢查一次(剛好趕上剛部署)。
 * 偵測到新版只「顯示橫幅」,由使用者決定何時重整。
 */
export function startVersionCheck(): void {
  const baseline = loadedBundleId();
  if (!baseline || typeof fetch === 'undefined') return;
  let shown = false;
  const run = async (): Promise<void> => {
    if (shown) return;
    const latest = await fetchLatestBundleId();
    if (latest && latest !== baseline) {
      shown = true;
      showUpdateBanner();
    }
  };
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') void run();
    });
  }
  setInterval(() => void run(), 5 * 60 * 1000);
  setTimeout(() => void run(), 30_000);
}
